<template>
    <div class="gs-order-bump-editor gs-order-bump-editor--postsell">
        <div v-if="!selectedPricePointId" class="gs-order-bump-editor__toolbar">
            <div>
                <label>Price point</label>
                <p>Choose the price point this post-sell bump belongs to.</p>
            </div>
            <select v-model="pricePointsTab" class="gs-order-bump-editor__select">
                <option v-for="tab in pricePointsTabs" :key="tab.value" :value="tab.value">
                    {{ tab.title }}
                </option>
            </select>
        </div>
        <div class="mt-4">
            <div v-for="(pricePoint, index) in localModel.pricing_points" :key="index">
                <div v-if="pricePointsTab === 'price_point_' + pricePoint.id" class="gs-order-bump-editor__panel">
                    <div class="gs-order-bump-editor__section">
                        <div class="gs-order-bump-editor__activation">
                            <button
                                v-for="(bumpType, bumpIndex) in (pricePoint.price > 0 ? orderBumpStatus : orderBumpTypesForFreePricePoint)"
                                :key="bumpIndex"
                                type="button"
                                class="gs-order-bump-editor__choice"
                                :class="{ 'is-selected': isActivationChoiceSelected(pricePoint, bumpType.id), 'is-disabled': bumpType.is_disabled }"
                                :disabled="bumpType.is_disabled"
                                @click="!bumpType.is_disabled && setBumpActivation(bumpType.id)"
                            >
                                <span></span>
                                <strong>{{ bumpType.title }}</strong>
                                <small>{{ bumpType.description }}</small>
                            </button>
                        </div>
                    </div>

                    <slot name="after-activation"></slot>

                    <div v-if="isPricePointBumpEnabled(pricePoint)" class="w-full flex flex-col">
                        <div class="gs-order-bump-editor__content">
                            <div class="gs-order-bump-editor__form">
                                <div class="w-full flex flex-col mb-6">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_heading')
                                    }}</label>
                                    <div class="w-full flex flex-row">
                                        <div class="w-full mr-2">
                                            <text-input
                                                v-model="pricePoint.post_sale_order_bump_options.post_order_heading_line"
                                                :validator="$v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_heading_line"
                                                :attribute="$t('frontend_heading')"
                                                value="pricePoint.post_sale_order_bump_options.post_order_heading_line"
                                                :placeholder="$t('frontend_heading')" />
                                        </div>
                                    </div>
                                </div>
                                <div class="w-full flex flex-col mb-6">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_sub_heading')
                                    }}</label>
                                    <div class="w-full flex flex-row">
                                        <div class="w-full mr-2">
                                            <text-input
                                                v-model="pricePoint.post_sale_order_bump_options.post_order_subheading"
                                                :validator="$v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_subheading"
                                                :attribute="$t('frontend_subheading')"
                                                value="pricePoint.post_sale_order_bump_options.post_order_subheading"
                                                :placeholder="$t('frontend_subheading')" />
                                        </div>
                                    </div>
                                </div>
                                <div class="w-full flex flex-col mb-6">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_desc')
                                    }}</label>
                                    <div class="w-full flex flex-row">
                                        <div class="w-full mr-2">
                                            <quill-editor
                                                ref="postSellOrderBumpEditor"
                                                v-model="pricePoint.post_sale_order_bump_options.post_order_description"
                                                class="min-h-32"
                                                :class="{'invalid': $v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_description.$error}"
                                                :options="postSellOrderBumpEditorOptions"
                                            >
                                            </quill-editor>
                                        </div>
                                    </div>
                                </div>

                                <div class="w-full flex flex-col mb-8">
                                    <div class="w-full flex">
                                        <div class="flex flex-col w-1/2">
                                            <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                                $t('frontend_show_video') }}?</label>
                                            <gd-toggle :aria-label="$t('frontend_show_video')" v-model="pricePoint.post_sale_order_bump_options
                                                .post_order_video_details
                                                .post_order_show_video
                                                " />
                                        </div>
                                    </div>
                                </div>

                                <div v-if="pricePoint.post_sale_order_bump_options
                                    .post_order_video_details.post_order_show_video
                                    " class="w-full flex flex-col mb-6">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_video_url')
                                    }}</label>

                                    <div class="w-full flex flex-row">
                                        <div class="w-full mr-2">
                                            <text-input
                                                v-model="pricePoint.post_sale_order_bump_options.post_order_video_details.url"
                                                attribute="Video URL"
                                                value="pricePoint.post_sale_order_bump_options.post_order_video_details.url"
                                                :validator="$v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_video_details.url"
                                                :placeholder="$t('frontend_product_step6_video_url_ph', {videoAppTitle: whitelabelAppData('video').title})"
                                                @input="updateUrl(index, $event)" />
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-col w-full mb-6">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_product')
                                    }}</label>
                                    <gs-name-select
                                        v-model="pricePoint.post_sale_order_bump_options.post_order_product_id"
                                        size="full"
                                        :placeholder="$t('frontend_select_product')"
                                        :class="{'invalid': $v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_product_id.$error}"
                                        :options="pricePointFunnelProductsOptions(pricePoint)"
                                        @change="changedPostSellProduct(pricePoint, $event)"
                                    />
                                    <span
                                        v-if="$v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_product_id.$error"
                                        class="text-danger text-sm"
                                    >
                                        {{ $t('frontend_product_required') }}
                                    </span>
                                </div>
                                <div class="flex flex-col w-full mb-6">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_price_point')
                                    }}</label>
                                    <gs-name-select
                                        v-model="pricePoint.post_sale_order_bump_options.post_order_price_point_id"
                                        size="full"
                                        :placeholder="$t('frontend_groovesell_select_price_point')"
                                        :class="{'invalid': $v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_price_point_id.$error}"
                                        :options="selectedProductPricePointsOptions(pricePoint, pricePoint.post_sale_order_bump_options.post_order_product_id)"
                                    />
                                    <span
                                        v-if="$v.localModel.pricing_points.$each[index].post_sale_order_bump_options.post_order_price_point_id.$error"
                                        class="text-danger text-sm"
                                    >
                                        {{ $t('frontend_price_point_required') }}
                                    </span>
                                </div>
                                <div class="w-full flex flex-col mb-6">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_style')
                                    }}</label>
                                    <div class="w-full flex flex-row">
                                        <div class="w-full">
                                            <gd-dropdown
                                                v-model="pricePoint.post_sale_order_bump_options.post_order_style_id"
                                                size="full"
                                                :options="postSellOrderBumpstyleOptionsList"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="w-full flex flex-col mb-8">
                                    <div class="w-full flex">
                                        <div class="flex flex-col w-1/2">
                                            <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                                $t('frontend_display_thumbnail') }}?</label>
                                            <gd-toggle
                                                :aria-label="$t('frontend_display_thumbnail')"
                                                v-model="pricePoint.post_sale_order_bump_options.post_order_display_thumbnail"
                                                @change="enabledThumbnail(index, $event)" />
                                        </div>
                                    </div>
                                </div>
                                <div class="w-full"
                                    v-if="pricePoint.post_sale_order_bump_options.post_order_display_thumbnail">
                                    <div class="w-full flex flex-col mb-8">
                                        <div class="w-full flex">
                                            <div class="flex flex-col w-1/2">
                                                <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                                    $t('frontend_thumbnail_image') }}</label>
                                                <image-picker
                                                    v-model="pricePoint.post_sale_order_bump_options.post_order_thumbnail_image"
                                                    :primary="true"></image-picker>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="w-full flex flex-row mb-8">
                                        <div class="w-1/2 flex flex-col mr-2">
                                            <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_style')
                                            }}</label>
                                            <div class="w-full flex flex-row">
                                                <gd-dropdown
                                                    v-model="pricePoint.post_sale_order_bump_options.post_order_thumbnail_size"
                                                    size="full"
                                                    class="mr-2"
                                                    :options="thumbnailSizeOptions"
                                                />
                                            </div>
                                        </div>
                                        <div class="w-1/2 flex flex-col">
                                            <div class="w-full flex">
                                                <div class="flex flex-col w-1/2">
                                                    <label class="
                                    font-semibold
                                    text-sm text-grey-700
                                    mb-2
                                  ">{{ $t('frontend_square_thumbnail') }}?</label>
                                                    <gd-toggle class="mt-3" :aria-label="$t('frontend_square_thumbnail')" v-model="pricePoint.post_sale_order_bump_options
                                                        .post_order_is_square
                                                        " />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="gs-order-bump-editor__preview">
                                <div class="w-full flex flex-col mb-6">
                                    <label class="
                            w-full
                            text-grey-dark text-sm
                            font-semibold
                            mb-32
                          ">{{ $t('frontend_preview') }}</label>
                                    <gd-button v-if="pricePoint.post_sale_order_bump_options
                                        .post_order_product_id &&
                                        pricePoint.post_sale_order_bump_options
                                            .post_order_price_point_id
                                        " size="auto" variant="primary" class="ml-4 mt-3" @click="
        emitPreviewPostSaleBump(
            pricePoint.post_sale_order_bump_options,
            pricePoint.funnelProducts,
            pricePoint.postSaleProductPricePoint
        )
        ">{{ $t('frontend_click_to_preview') }}</gd-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import urlParser from 'js-video-url-parser/lib/base';
import Swal from 'sweetalert2';
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';
import { quillEditor } from 'vue-quill-editor';
import {
  required,
  minLength,
  requiredIf,
} from 'vuelidate/lib/validators';

export default {
    components: {
        ImagePicker,
        quillEditor,
    },
    props: {
        model: {
            required: true
        },
        selectedPricePointId: {
            default: null,
        },
        isBumpEnabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localModel: this.model,
            pricePointsTab: '',
            orderBumpStatus: [
                {
                    id: 0,
                    title: this.$t('frontend_product_step6_no_dont'),
                    description: this.$t('frontend_product_step6_the_checkout'),
                },
                {
                    id: 1,
                    title: this.$t('frontend_product_step6_yes_activate'),
                    description: this.$t('frontend_product_step6_the_checkout_page'),
                },
            ],
            orderBumpTypesForFreePricePoint: [
                {
                    id: 0,
                    title: this.$t('frontend_product_step6_no_dont'),
                    description: this.$t('frontend_product_step6_the_checkout'),
                },
                {
                    id: 1,
                    title: this.$t('frontend_product_step6_yes_activate'),
                    description: this.$t('frontend_product_step6_the_checkout_page'),
                    is_disabled: true,
                },
            ],
            postSellOrderBumpEditorOptions: {
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],

                        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
                    ],
                },
            },
            offerBumpComponentKey: 1,
            postSellOrderBumpstyleOptionsList: [
                { text: 'Light Box', value: 1 },
                { text: 'Slider', value: 2 },
            ]
        }
    },
    computed: {
        thumbnailSizeOptions() {
            return [
                { text: this.$t('frontend_small'), value: 'sm' },
                { text: this.$t('frontend_normal'), value: 'md' },
                { text: this.$t('frontend_large'), value: 'lg' },
            ];
        },
        pricePointsTabs() {
            const pricePointTabs = [];
            for (let i = 0; i < this.localModel.pricing_points.length; i++) {
                pricePointTabs.push({
                    title: this.localModel.pricing_points[i].name,
                    value: `price_point_${this.localModel.pricing_points[i].id}`,
                });
            }
            return pricePointTabs;
        },
        pricePointsErrorTabs() {
            const pricePointErrorTabs = [];

            let pricePointIndex;
            for (pricePointIndex in this.$v.localModel.pricing_points.$each.$iter) {
                if (
                this.$v.localModel.pricing_points.$each.$iter[pricePointIndex].$error
                ) {
                pricePointErrorTabs.push(
                    `price_point_${this.$v.localModel.pricing_points.$each.$iter[pricePointIndex].$model.id}`
                );
                }
            }

            return pricePointErrorTabs;
        },
    },
    mounted() {
        this.syncSelectedPricePoint();
    },
    watch: {
        selectedPricePointId() {
            this.syncSelectedPricePoint();
        },
        localModel: {
            handler: function (val, oldVal) {
                this.$emit('update-model', val);
            },
            deep: true
        },
    },
    methods: {
        syncSelectedPricePoint() {
            if (this.selectedPricePointId) {
                this.pricePointsTab = `price_point_${this.selectedPricePointId}`;
                return;
            }

            if (this.pricePointsTab == '' && this.localModel.pricing_points.length > 0) {
                this.pricePointsTab = `price_point_${this.localModel.pricing_points[0].id}`;
            }
        },
        isSelectedPricePoint(pricePoint) {
            return this.selectedPricePointId && pricePoint.id === this.selectedPricePointId;
        },
        isPricePointBumpEnabled(pricePoint) {
            const postSellEnabled = pricePoint.post_sale_order_bump_options &&
                Number(pricePoint.post_sale_order_bump_options.post_order_bump_enable);

            return this.isSelectedPricePoint(pricePoint) ? this.isBumpEnabled : !!postSellEnabled;
        },
        isActivationChoiceSelected(pricePoint, value) {
            const enabled = this.isPricePointBumpEnabled(pricePoint);
            return Number(value) === (enabled ? 1 : 0);
        },
        setBumpActivation(value) {
            this.$emit('set-bump-enabled', {
                enabled: Number(value) === 1,
                type: 'postsell',
            });
        },
        pricePointFunnelProductsOptions(pricePoint) {
            return pricePoint.funnelProducts.map((product) => {
                return {
                    text: product.name,
                    value: product.id,
                };
            });
        },
        selectedProductPricePointsOptions(pricePoint) {
            if (!pricePoint.postSaleProductPricePoint) {
                return [];
            }

            return pricePoint.postSaleProductPricePoint.map((pricePoint) => {
                return {
                    text: pricePoint.name,
                    value: pricePoint.id,
                };
            });
        },
        changedPostSellProduct(pricePoint, event) {
            pricePoint.post_sale_order_bump_options.post_order_price_point_id = 0;
            pricePoint.funnelProducts.find((product) => {
                if (product.id == event) {
                    pricePoint.postSaleProductPricePoint = product.price_points;
                }
            });
        },
        emitPreviewPostSaleBump(postSaleOrderBumpOptions, funnelProducts, postSaleProductPricePoint) {
            this.$emit('preview-post-sale-bump', postSaleOrderBumpOptions, funnelProducts, postSaleProductPricePoint);
        },
        enabledThumbnail(index, event) {
            if (event) {
                if (this.localModel.pricing_points[index].post_sale_order_bump_options.post_order_thumbnail_image == '' && this.localModel.checkout_page_options.product_image != '') {
                    this.localModel.pricing_points[index].post_sale_order_bump_options.post_order_thumbnail_image = this.localModel.checkout_page_options.product_image;
                }
            }
        },
        updateUrl(index, event) {
            const videoInfo = urlParser.parse(
                this.localModel.pricing_points[index].post_sale_order_bump_options
                    .post_order_video_details.url
            );
            // urlParser.parse(value) === undefined
            if (videoInfo !== undefined && videoInfo.provider == 'youtube') {
                this.localModel.pricing_points[
                    index
                ].post_sale_order_bump_options.post_order_video_details.provider =
                    'youtube';
                this.localModel.pricing_points[
                    index
                ].post_sale_order_bump_options.post_order_video_details.id =
                    videoInfo.id;
            } else if (videoInfo !== undefined && videoInfo.provider == 'vimeo') {
                this.localModel.pricing_points[
                    index
                ].post_sale_order_bump_options.post_order_video_details.provider =
                    'vimeo';
                this.localModel.pricing_points[
                    index
                ].post_sale_order_bump_options.post_order_video_details.id =
                    videoInfo.id;
            } else if (videoInfo !== undefined && videoInfo.provider == 'wistia') {
                this.localModel.pricing_points[
                    index
                ].post_sale_order_bump_options.post_order_video_details.provider =
                    'wistia';
                this.localModel.pricing_points[
                    index
                ].post_sale_order_bump_options.post_order_video_details.id =
                    videoInfo.id;
            } else {
                const brandAltName = this.$store.state.brandData.brandAltName.toLowerCase();
                const videoAppTitle = this.whitelabelAppData('video').title.toLowerCase();
                const expression = new RegExp(`(http:|https:|)\/\/?(app\\.${brandAltName}\\.com)\/${videoAppTitle}\/videopage\/[A-Za-z0-9]*\/[A-Za-z0-9]*`, 'g');

                const regex = new RegExp(expression);
                const tempUrl =
                    this.localModel.pricing_points[index].post_sale_order_bump_options
                        .post_order_video_details.url;

                if (tempUrl.match(regex)) {
                    const splitUrl =
                        this.localModel.pricing_points[
                            index
                        ].post_sale_order_bump_options.post_order_video_details.url.split(
                            '/videopage/'
                        );
                    const splitId = splitUrl[1].split('/');
                    const videoId = splitId[0];
                    const videoPermalink = splitId[1];
                    const videoAppTitle = this.whitelabelAppData('video').title.toLowerCase();

                    this.localModel.pricing_points[
                        index
                    ].post_sale_order_bump_options.post_order_video_details.provider = videoAppTitle;
                    this.localModel.pricing_points[
                        index
                    ].post_sale_order_bump_options.post_order_video_details.id = videoId;
                    this.localModel.pricing_points[
                        index
                    ].post_sale_order_bump_options.post_order_video_details.permalink = videoPermalink;
                }
            }
        },
        handlePricePointsTabChange(tab) {
            this.pricePointsTab = tab;
        },
        createOfferBump(index) {
            this.localModel.pricing_points[index].offer_bump_options.push({
                display_thumbnail: 0,
                display_bouncing: 0,
                heading_line: '',
                description: '',
                style_id: 1,
                product_id: 0,
                price_point_id: 0,
            });
        },
        async deleteOfferBump(index, offerBumpIndex) {
            const { value: accept } = await Swal.fire({
                title: this.$t('frontend_sure'),
                text: this.$t('frontend_non_revertable'),
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('frontend_delete_confirm'),
            });
            if (accept) {
                this.localModel.pricing_points[index].offer_bump_options.splice(
                    offerBumpIndex,
                    1
                );
            }

           
        },
        selectedProductPricePoints(pricePoint, productId) {
            const funnelProduct = pricePoint.funnelProducts.find(
                (product) => product.id == productId
            );

            if (funnelProduct) {
                return funnelProduct.price_points;
            }
            return [];
        },
    },
    validations: {
        localModel: {
            pricing_points: {
                required,
                minLength: minLength(1),
                $each: {
                    post_sale_order_bump_options: {
                        post_order_heading_line: {
                            required: requiredIf((object) => {
                                if (
                                    object.post_order_bump_enable &&
                                    object.post_order_bump_enable == 1
                                ) {
                                    return true;
                                }

                                return false;
                            }),
                        },
                        post_order_subheading: {
                            required: requiredIf((object) => {
                                if (
                                    object.post_order_bump_enable &&
                                    object.post_order_bump_enable == 1
                                ) {
                                    return true;
                                }

                                return false;
                            }),
                        },
                        post_order_description: {
                            required: requiredIf((object) => {
                                if (
                                    object.post_order_bump_enable &&
                                    object.post_order_bump_enable == 1
                                ) {
                                    return true;
                                }
                                return false;
                            }),
                        },
                        post_order_product_id: {
                            isValidProductId(value, object) {
                                if (
                                    object.post_order_bump_enable &&
                                    object.post_order_bump_enable == 1 &&
                                    (!value || value == 0)
                                ) {
                                    return false;
                                }
                                return true;
                            },
                        },
                        post_order_price_point_id: {
                            isValidPricePointtId(value, object) {
                                if (
                                    object.post_order_bump_enable &&
                                    object.post_order_bump_enable == 1 &&
                                    (!value || value == 0)
                                ) {
                                    return false;
                                }
                                return true;
                            },
                        },
                        post_order_video_details: {
                            url: {
                                required: requiredIf((object) => {
                                    if (
                                        object.post_order_show_video &&
                                        object.post_order_show_video == true
                                    ) {
                                        return true;
                                    }
                                    return false;
                                }),
                                isValidPostSaleVideoUrl(value, object) {
                                    if (
                                        object.post_order_show_video &&
                                        object.post_order_show_video == true
                                    ) {
                                        value = value || '';
                                        const videoInfo = urlParser.parse(value);

                                        if (videoInfo != undefined) {
                                            return true;
                                        }
                                        const brandAltName = this.$store.state.brandData.brandAltName.toLowerCase();
                                        const videoAppTitle = this.whitelabelAppData('video').title.toLowerCase();
                                        const expression = new RegExp(`(http:|https:|)\/\/?(app\\.${brandAltName}\\.com)\/${videoAppTitle}\/videopage\/[A-Za-z0-9]*\/[A-Za-z0-9]*`, 'g');
                                        const regex = new RegExp(expression);
                                        if (value.match(regex)) {
                                            return true;
                                        }
                                        return false;
                                    }
                                    return true;
                                },
                            },
                        },
                    },
                },
            },
        },
    },
}
</script>

<style>
.invalid .border-grey-200  {
    border-color: var(--gs-danger);
}
</style>

<style scoped>
.gs-order-bump-editor {
    color: var(--gs-foreground);
    overflow-x: hidden;
}

.gs-order-bump-editor__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 16px 18px;
    margin-bottom: 18px;
    background: var(--gs-surface-subtle);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
}

.gs-order-bump-editor__toolbar label {
    display: block;
    color: var(--gs-foreground);
    font-size: 16px;
    font-weight: 600;
    line-height: 1.25;
}

.gs-order-bump-editor__toolbar p {
    margin: 4px 0 0;
    color: var(--gs-muted-foreground);
    font-size: 14px;
    line-height: 1.35;
}

.gs-order-bump-editor__select {
    width: min(360px, 44%);
    min-height: 44px;
    padding: 0 14px;
    color: var(--gs-foreground);
    font-size: 15px;
    font-weight: 500;
    background: #fff;
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-sm);
}

.gs-order-bump-editor__panel {
    min-width: 0;
}

.gs-order-bump-editor__section {
    margin-bottom: 20px;
}

.gs-order-bump-editor__activation {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    max-width: 640px;
}

.gs-order-bump-editor__choice {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 6px 12px;
    min-height: 112px;
    padding: 18px;
    text-align: left;
    background: var(--gs-surface);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
    cursor: pointer;
}

.gs-order-bump-editor__choice span {
    width: 22px;
    height: 22px;
    border: 2px solid var(--gs-border-strong);
    border-radius: 50%;
}

.gs-order-bump-editor__choice strong {
    color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
    line-height: 1.25;
}

.gs-order-bump-editor__choice small {
    grid-column: 2;
    color: var(--gs-muted-foreground);
    font-size: 14px;
    line-height: 1.35;
}

.gs-order-bump-editor__choice.is-selected {
    background: var(--gs-active-soft);
    border-color: var(--gs-active);
    box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-order-bump-editor__choice.is-selected span {
    border-color: var(--gs-active);
    box-shadow: inset 0 0 0 4px var(--gs-surface);
    background: var(--gs-active);
}

.gs-order-bump-editor__choice.is-disabled {
    opacity: .55;
    cursor: not-allowed;
}

.gs-order-bump-editor__content {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 320px);
    gap: 24px;
    align-items: start;
    width: 100%;
    min-width: 0;
}

.gs-order-bump-editor__form,
.gs-order-bump-editor__preview {
    min-width: 0;
}

.gs-order-bump-editor__preview {
    padding-left: 22px;
    border-left: 1px solid var(--gs-border);
}

@media (max-width: 980px) {
    .gs-order-bump-editor__toolbar,
    .gs-order-bump-editor__content {
        display: block;
    }

    .gs-order-bump-editor__select {
        width: 100%;
        margin-top: 12px;
    }

    .gs-order-bump-editor__activation {
        grid-template-columns: 1fr;
    }

    .gs-order-bump-editor__preview {
        margin-top: 22px;
        padding-left: 0;
        border-left: 0;
    }
}
</style>
